import type { User } from "oidc-client-ts";

/** Декодирует payload JWT (без проверки подписи — её делает backend). */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = atob(padded);
    // atob возвращает binary string; пересобираем UTF-8 чтобы корректно
    // прочитать не-ASCII (cyrillic в claims, например).
    const bytes = new Uint8Array(json.length);
    for (let i = 0; i < json.length; i++) bytes[i] = json.charCodeAt(i);
    return JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    return null;
  }
}

interface KcRealmAccess {
  roles?: string[];
}

interface KcResourceAccess {
  [client: string]: { roles?: string[] };
}

/** Достаёт все роли пользователя из access_token.
 *
 *  Поддерживает оба IdP:
 *  - Keycloak: `realm_access.roles` + `resource_access.<client>.roles`
 *  - Authentik: стандартный OIDC `groups` claim
 *  Возвращает дедуплицированный список — порядок: realm → groups → client. */
export function getUserRoles(user: User | null | undefined): string[] {
  if (!user?.access_token) return [];
  const payload = decodeJwtPayload(user.access_token);
  if (!payload) return [];
  const realmRoles =
    (payload["realm_access"] as KcRealmAccess | undefined)?.roles ?? [];
  const groups = Array.isArray(payload["groups"])
    ? (payload["groups"] as unknown[]).map(String)
    : [];
  const resource = (payload["resource_access"] as KcResourceAccess | undefined) ?? {};
  const clientRoles: string[] = [];
  for (const client of Object.keys(resource)) {
    for (const role of resource[client]?.roles ?? []) {
      clientRoles.push(`${client}:${role}`);
    }
  }
  const all = [...realmRoles, ...groups, ...clientRoles];
  return Array.from(new Set(all));
}

/** Quick check: пользователь имеет указанную роль (через realm_access или groups). */
export function hasRealmRole(
  user: User | null | undefined,
  role: string,
): boolean {
  const roles = getUserRoles(user);
  return roles.includes(role);
}

/** Username из id_token профиля. */
export function getUsername(user: User | null | undefined): string {
  const profile = user?.profile as
    | { preferred_username?: string; email?: string }
    | undefined;
  return profile?.preferred_username ?? profile?.email ?? "—";
}
